import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Repeat, IndianRupee, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { trackUpgradeLead } from "@/lib/analytics";

export default function UpgradeForm({ compact = false }) {
    const [form, setForm] = useState({
        name: "",
        mobile: "",
        city: "",
        current_brand: "",
        current_model: "",
        year_of_purchase: "",
        warranty_status: "",
        working_condition: "",
        preferred_clinic: "",
    });
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.mobile) {
            toast.error("Name and mobile are required");
            return;
        }
        setLoading(true);
        try {
            await api.post("/leads", {
                type: "upgrade",
                ...form,
                source_page: "/upgrade-form",
                whatsapp_consent: true,
            });
            try { trackUpgradeLead({ currentBrand: form.current_brand, currentModel: form.current_model }); } catch (e) { /* never break */ }
            toast.success("Our Styletto expert will contact you shortly.");
            navigate("/upgrade?submitted=1");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            className={`${compact ? "" : "section"} bg-gradient-to-br from-[#003E4D] to-[#002D38] text-white relative overflow-hidden`}
            data-testid="upgrade-section"
        >
            <div className="absolute inset-0 opacity-[0.07]" style={{
                backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.6) 1px, transparent 1px)",
                backgroundSize: "30px 30px",
            }} />
            <div className="container-x relative">
                <div className="grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5">
                        <span className="eyebrow !text-[#7DCDD2] inline-flex items-center gap-2">
                            <Repeat size={12} /> Upgrade Program
                        </span>
                        <h2 className="font-serif text-[40px] md:text-[56px] leading-[1.04] mt-3">
                            Already wearing a hearing aid?
                            <br />
                            <span className="italic text-[#7DCDD2]">Upgrade to Styletto IX.</span>
                        </h2>
                        <ul className="mt-8 space-y-3 text-[15.5px] text-white/85">
                            <li className="flex items-start gap-3">
                                <IndianRupee size={18} className="text-[#7DCDD2] mt-0.5 shrink-0" />
                                <span>Get up to <b>40–45% upgrade benefit</b> on your old hearing aid (subject to device condition and audiologist evaluation).</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Sparkles size={18} className="text-[#7DCDD2] mt-0.5 shrink-0" />
                                <span>Try the latest Signia <b>Integrated Xperience</b> technology with full Bluetooth & rechargeable convenience.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <ArrowRight size={18} className="text-[#7DCDD2] mt-0.5 shrink-0" />
                                <span>Free expert evaluation by RCI-certified audiologists — at home or any HearClear clinic.</span>
                            </li>
                        </ul>
                    </div>

                    <form
                        onSubmit={submit}
                        className="lg:col-span-7 bg-white text-[#1a1a1a] rounded-[28px] p-6 md:p-10 shadow-[0_30px_70px_-25px_rgba(0,0,0,0.35)]"
                        data-testid="upgrade-form"
                    >
                        <h3 className="font-serif text-[28px] md:text-[34px] leading-tight">Check Upgrade Eligibility</h3>
                        <p className="text-[14px] text-[#4a5568] mt-2">Takes 30 seconds. We'll contact you within 24 hours.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <Input data-testid="upgrade-name" required placeholder="Full Name *" value={form.name} onChange={set("name")} />
                            <Input data-testid="upgrade-mobile" required placeholder="Mobile *" value={form.mobile} onChange={set("mobile")} />
                            <Input data-testid="upgrade-city" placeholder="City" value={form.city} onChange={set("city")} />
                            <Input data-testid="upgrade-brand" placeholder="Current brand (e.g. Signia, Phonak…)" value={form.current_brand} onChange={set("current_brand")} />
                            <Input data-testid="upgrade-model" placeholder="Current model" value={form.current_model} onChange={set("current_model")} />
                            <Input data-testid="upgrade-year" placeholder="Year of purchase" value={form.year_of_purchase} onChange={set("year_of_purchase")} />
                            <Select data-testid="upgrade-warranty" value={form.warranty_status} onChange={set("warranty_status")}>
                                <option value="">Warranty status</option>
                                <option>In warranty</option>
                                <option>Out of warranty</option>
                                <option>Not sure</option>
                            </Select>
                            <Select data-testid="upgrade-condition" value={form.working_condition} onChange={set("working_condition")}>
                                <option value="">Working condition</option>
                                <option>Working perfectly</option>
                                <option>Working with minor issues</option>
                                <option>Not working</option>
                            </Select>
                            <Input data-testid="upgrade-clinic" placeholder="Preferred clinic (optional)" value={form.preferred_clinic} onChange={set("preferred_clinic")} className="md:col-span-2" />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary mt-7 w-full justify-center md:w-auto !px-10"
                            data-testid="upgrade-submit"
                        >
                            {loading ? "Submitting…" : "Check Upgrade Eligibility"}
                            <ArrowRight size={16} />
                        </button>
                        <p className="text-[12px] text-[#9CA3AF] mt-4">
                            By submitting, you consent to be contacted by HearClear via WhatsApp/phone for upgrade evaluation.
                        </p>
                    </form>
                </div>
            </div>
        </section>
    );
}

const Input = ({ className = "", ...rest }) => (
    <input
        className={`h-12 px-4 rounded-xl border border-[#E5E7EB] bg-white text-[15px] focus:outline-none focus:border-[#003E4D] focus:ring-2 focus:ring-[#003E4D]/15 transition ${className}`}
        {...rest}
    />
);

const Select = ({ className = "", children, ...rest }) => (
    <select
        className={`h-12 px-3 rounded-xl border border-[#E5E7EB] bg-white text-[15px] focus:outline-none focus:border-[#003E4D] focus:ring-2 focus:ring-[#003E4D]/15 transition ${className}`}
        {...rest}
    >
        {children}
    </select>
);
